/**
 * What the five published `/use-cases-categories/…` pages say.
 *
 * One record per page, editorial like `home/-components/homeContent.ts`:
 * nothing derives it. Read off the five published documents, which are one
 * Webflow collection template: a breadcrumb, a title, a line, and the cards.
 *
 * The cards are deliberately not authored here. A category page shows exactly
 * the use-case pages whose card carries this one's `name`, which is how the
 * published collection page is filtered, so the two card sets cannot drift.
 */
import type { UseCaseCategorySlug } from "@/content/useCaseCategoryPages";
import type { UseCaseCard } from "@/content/landing/types";

export type UseCaseCategory = {
  slug: UseCaseCategorySlug;
  /** `UseCaseRecord.category` verbatim: that match is what selects the cards. */
  name: string;
  /** `body_new is-small`, the single line under the title. */
  intro: string;
  /**
   * Set on four of the five. Not a rule with an exception but the published
   * site's own inconsistency, kept rather than tidied: `social-media` ships no
   * robots meta while the other four ship `noindex`. None of the five is in the
   * published sitemap, so indexability is the only thing this decides.
   */
  noindex?: true;
};

/**
 * The frame every one of the five titles is built on; the middle is the page's
 * own name. The published `<title>`s carry no "| Webshare" suffix, unlike the
 * `/use-cases` index they hang off.
 *
 * Named around `category` rather than the page family, here and below, because
 * a `useCaseCategory…` function reads to the hooks lint rule as a React hook.
 */
export function categoryTitle(category: UseCaseCategory): string {
  return `${category.name} Proxy Use Cases`;
}

/**
 * The cards the published page lists: every use-case page filed under this
 * category, in the index's own order. The set is not authored here, so a
 * twelfth use case joins its category page by existing.
 */
export function categoryCards(
  category: UseCaseCategory,
  cards: ReadonlyArray<UseCaseCard>
): Array<UseCaseCard> {
  return cards.filter((card) => card.category === category.name);
}

export const USE_CASE_CATEGORIES: ReadonlyArray<UseCaseCategory> = [
  {
    slug: "pricing-intelligence",
    name: "Pricing Intelligence",
    intro:
      "Webshare proxies will enable you to take your pricing intelligence to the next level. Implement price analysis, ecommerce scraping & take advantage of public product price data.",
    noindex: true,
  },
  {
    slug: "anti-fraud",
    name: "Anti-Fraud",
    intro:
      "Webshare is a trusted Proxy provider among Ad Verification & Monitoring service providers. Our global proxy network will serve your technical needs.",
    noindex: true,
  },
  {
    slug: "social-media",
    name: "Social Media",
    intro:
      "Proxies for social media account management, anonymous browsing and scraping are extremely popular among our users.",
  },
  {
    slug: "public-data",
    name: "Public Data",
    intro:
      "Gather publicly available data for your business needs with the help of proxies. Conduct marketing, SEO or financial research without errors.",
    noindex: true,
  },
  {
    slug: "reputation-management",
    name: "Reputation Management",
    intro:
      "Manage business or personal reputation at scale with assistance of proxies. Conduct continuous brand monitoring & market research.",
    noindex: true,
  },
];

/** Undefined for a slug the array does not carry, which the route 404s on. */
export function findCategory(slug: string): UseCaseCategory | undefined {
  return USE_CASE_CATEGORIES.find((category) => category.slug === slug);
}
